import type { Palette } from '@d2/formats';
import { Atlas, ATLAS_SIZE, Sprite } from './atlas';
import { compile, uniforms } from './gl';

/** Logical resolution of the game (LoD). Everything draws in these coordinates. */
export const VIEW_W = 800;
export const VIEW_H = 600;

export type BlendMode = 'normal' | 'add' | 'multiply' | 'screen';

export interface DrawOpts {
  /** Palette row from `renderer.palettes`. Defaults to the current default palette. */
  palette?: number;
  /** Index remap row from `renderer.remaps` (text colors, monster/item color maps, light). */
  remap?: number;
  alpha?: number;
  /** Multiplies the final color (0..1 each, may exceed 1 for brightening up to 2). */
  tint?: readonly [number, number, number];
  blend?: BlendMode;
  /** Ignore the frame's anchor offsets; (x, y) is then the top-left corner. */
  topLeft?: boolean;
  scale?: number;
  flipX?: boolean;
  /** Treat index 0 as an opaque color (DT1 floors sometimes use it); default transparent. */
  opaqueZero?: boolean;
  /** Draw the sprite as a flat-colored silhouette (shadows, selection outlines). */
  silhouette?: readonly [number, number, number];
  /** Vertical squash and horizontal shear around the anchor (ground shadows). */
  squashY?: number;
  shearX?: number;
}

const FLOATS_PER_VERT = 13;
const MAX_QUADS = 16384;

const VS = `#version 300 es
layout(location=0) in vec2 aPos;
layout(location=1) in vec3 aUv;      // u, v (texels), layer
layout(location=2) in vec3 aPal;     // palette row, remap row (-1 none), mode
layout(location=3) in vec4 aColor;   // tint rgb, alpha
layout(location=4) in float aFlags;
uniform vec2 uView;
out vec3 vUv; out vec3 vPal; out vec4 vColor; flat out float vFlags;
void main() {
  vec2 p = aPos / uView * 2.0 - 1.0;
  gl_Position = vec4(p.x, -p.y, 0.0, 1.0);
  vUv = aUv; vPal = aPal; vColor = aColor; vFlags = aFlags;
}`;

// mode: 0 indexed sprite, 1 solid color, 2 silhouette of an indexed sprite (uses vColor as the color)
const FS = `#version 300 es
precision highp float;
precision highp sampler2DArray;
uniform sampler2DArray uAtlas;
uniform sampler2D uPalettes;   // 256 x N RGBA
uniform sampler2D uRemaps;     // 256 x M R8
in vec3 vUv; in vec3 vPal; in vec4 vColor; flat in float vFlags;
out vec4 outColor;
void main() {
  float mode = vPal.z;
  if (mode > 0.5 && mode < 1.5) { outColor = vColor; return; }
  float idx = texelFetch(uAtlas, ivec3(ivec2(vUv.xy), int(vUv.z)), 0).r * 255.0;
  bool opaqueZero = mod(vFlags, 2.0) > 0.5;
  if (idx < 0.5 && !opaqueZero) discard;
  if (mode > 1.5) { outColor = vColor; return; }
  if (vPal.y >= 0.0) idx = texelFetch(uRemaps, ivec2(int(idx + 0.5), int(vPal.y)), 0).r * 255.0;
  vec4 c = texelFetch(uPalettes, ivec2(int(idx + 0.5), int(vPal.x)), 0);
  outColor = vec4(c.rgb * vColor.rgb, vColor.a);
}`;

// Lighting pass: multiplies the scene by ambient + up to 16 soft point lights (screen space).
const LIGHT_FS = `#version 300 es
precision highp float;
uniform vec3 uAmbient;
uniform int uCount;
uniform vec4 uLights[16];   // x, y (view px), radius (px), intensity
uniform vec3 uColors[16];
uniform vec2 uView;
in vec2 vUv; out vec4 outColor;
void main() {
  vec2 p = vec2(vUv.x, 1.0 - vUv.y) * uView;
  vec3 l = uAmbient;
  for (int i = 0; i < 16; i++) {
    if (i >= uCount) break;
    vec4 L = uLights[i];
    // Isometric light pools: squash vertically so the radius reads as a ground circle.
    vec2 d = (p - L.xy) * vec2(1.0, 2.0);
    // Full strength over the inner third of the radius, then a long smooth fall to dark
    // (the original's light radius reads as a bright pool, not a cone fading from the centre).
    float f = clamp((1.0 - length(d) / L.z) / 0.65, 0.0, 1.0);
    l += uColors[i] * L.w * (f * f * (3.0 - 2.0 * f));
  }
  outColor = vec4(min(l, vec3(1.25)), 1.0);
}`;

export interface PointLight {
  x: number;
  y: number;
  radius: number;
  intensity: number;
  color?: readonly [number, number, number];
}

const PRESENT_VS = `#version 300 es
layout(location=0) in vec2 aPos;
out vec2 vUv;
void main() { vUv = aPos * 0.5 + 0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

// "Sharp bilinear": nearest-neighbour inside texels, a 1-screen-pixel blend at texel edges, so
// non-integer scales stay crisp without shimmering. Gamma and contrast (the Video Options
// sliders) are applied here, to the whole frame like the original's palette gamma ramp.
const PRESENT_FS = `#version 300 es
precision highp float;
uniform sampler2D uTex;
uniform vec2 uTexSize;
uniform float uScale;
uniform float uGamma;
uniform float uContrast;
in vec2 vUv; out vec4 outColor;
void main() {
  vec2 texel = vUv * uTexSize;
  vec2 f = fract(texel);
  vec2 w = clamp((f - 0.5) * uScale + 0.5, 0.0, 1.0);
  vec2 uv = (floor(texel) + w) / uTexSize;
  vec3 c = texture(uTex, uv).rgb;
  c = pow(c, vec3(1.0 / uGamma));
  c = clamp((c - 0.5) * uContrast + 0.5, 0.0, 1.0);
  outColor = vec4(c, 1.0);
}`;

/** Rows of 256-entry tables uploaded to a texture; looked up by name. */
class RowBank {
  readonly texture: WebGLTexture;
  private readonly rows = new Map<string, number>();
  private count = 0;
  constructor(
    private readonly gl: WebGL2RenderingContext,
    private readonly capacity: number,
    private readonly rgba: boolean,
  ) {
    this.texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texStorage2D(gl.TEXTURE_2D, 1, rgba ? gl.RGBA8 : gl.R8, 256, capacity);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  }
  /** Registers (or replaces) a row; returns its index. */
  set(name: string, data: Uint8Array): number {
    let row = this.rows.get(name);
    if (row === undefined) {
      if (this.count >= this.capacity) throw new Error('row bank full');
      row = this.count++;
      this.rows.set(name, row);
    }
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, row, 256, 1, this.rgba ? gl.RGBA : gl.RED, gl.UNSIGNED_BYTE, data);
    return row;
  }
  get(name: string): number | undefined {
    return this.rows.get(name);
  }
}

export class Renderer {
  readonly gl: WebGL2RenderingContext;
  readonly atlas: Atlas;
  readonly palettes: RowBank;
  readonly remaps: RowBank;
  defaultPalette = 0;

  private readonly prog: WebGLProgram;
  private readonly u: Record<string, WebGLUniformLocation>;
  private readonly vao: WebGLVertexArrayObject;
  private readonly vbo: WebGLBuffer;
  private readonly verts = new Float32Array(MAX_QUADS * 4 * FLOATS_PER_VERT);
  private quads = 0;
  private blend: BlendMode = 'normal';
  private target: { w: number; h: number } = { w: VIEW_W, h: VIEW_H };

  private readonly fbo: WebGLFramebuffer;
  private readonly fboTex: WebGLTexture;
  private readonly presentProg: WebGLProgram;
  private readonly presentU: Record<string, WebGLUniformLocation>;
  private readonly quadVao: WebGLVertexArrayObject;
  private clip: [number, number, number, number] | null = null;
  private readonly lightProg: WebGLProgram;
  private readonly lightU: Record<string, WebGLUniformLocation>;

  /** Display gamma exponent (1 = unchanged) and contrast factor (1 = unchanged). */
  gamma = 1;
  contrast = 1;
  /** Letterbox mapping from canvas pixels to logical view coordinates. */
  viewport = { x: 0, y: 0, w: VIEW_W, h: VIEW_H, scale: 1 };
  drawCalls = 0;
  private callsThisFrame = 0;

  constructor(readonly canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', { antialias: false, alpha: false, premultipliedAlpha: false, preserveDrawingBuffer: false });
    if (!gl) throw new Error('WebGL2 is required');
    this.gl = gl;
    this.atlas = new Atlas(gl);
    this.atlas.onReset = () => this.flush();
    this.palettes = new RowBank(gl, 64, true);
    this.remaps = new RowBank(gl, 1024, false);
    this.remaps.set('identity', Uint8Array.from({ length: 256 }, (_, i) => i));

    this.prog = compile(gl, VS, FS);
    this.u = uniforms(gl, this.prog, ['uView', 'uAtlas', 'uPalettes', 'uRemaps']);
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    this.vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, this.verts.byteLength, gl.DYNAMIC_DRAW);
    const stride = FLOATS_PER_VERT * 4;
    const attr = (loc: number, size: number, off: number) => {
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, stride, off * 4);
    };
    attr(0, 2, 0);
    attr(1, 3, 2);
    attr(2, 3, 5);
    attr(3, 4, 8);
    attr(4, 1, 12);
    const idx = new Uint32Array(MAX_QUADS * 6);
    for (let i = 0; i < MAX_QUADS; i++) idx.set([i * 4, i * 4 + 1, i * 4 + 2, i * 4 + 2, i * 4 + 1, i * 4 + 3], i * 6);
    const ibo = gl.createBuffer()!;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
    gl.bindVertexArray(null);

    this.fboTex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.fboTex);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA8, VIEW_W, VIEW_H);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.fbo = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.fboTex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    this.lightProg = compile(gl, PRESENT_VS, LIGHT_FS);
    this.lightU = uniforms(gl, this.lightProg, ['uAmbient', 'uCount', 'uLights', 'uColors', 'uView']);
    this.presentProg = compile(gl, PRESENT_VS, PRESENT_FS);
    this.presentU = uniforms(gl, this.presentProg, ['uTex', 'uTexSize', 'uScale', 'uGamma', 'uContrast']);
    this.quadVao = gl.createVertexArray()!;
    gl.bindVertexArray(this.quadVao);
    const qb = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, qb);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
  }

  setPalette(name: string, palette: Palette): number {
    return this.palettes.set(name, palette);
  }

  /** Fits the logical view into the canvas, integer-scaled when the window allows it. */
  resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const cw = Math.round(this.canvas.clientWidth * dpr);
    const ch = Math.round(this.canvas.clientHeight * dpr);
    if (this.canvas.width !== cw || this.canvas.height !== ch) {
      this.canvas.width = cw;
      this.canvas.height = ch;
    }
    let scale = Math.min(cw / VIEW_W, ch / VIEW_H);
    if (scale >= 1 && scale - Math.floor(scale) < 0.08) scale = Math.floor(scale);
    const w = Math.round(VIEW_W * scale);
    const h = Math.round(VIEW_H * scale);
    this.viewport = { x: Math.floor((cw - w) / 2), y: Math.floor((ch - h) / 2), w, h, scale };
  }

  /** Converts a mouse event position to logical coordinates. */
  toView(clientX: number, clientY: number): [number, number] {
    const r = this.canvas.getBoundingClientRect();
    const dpr = this.canvas.width / r.width;
    const px = (clientX - r.left) * dpr;
    const py = (clientY - r.top) * dpr;
    return [((px - this.viewport.x) / this.viewport.w) * VIEW_W, ((py - this.viewport.y) / this.viewport.h) * VIEW_H];
  }

  begin(): void {
    const gl = this.gl;
    this.callsThisFrame = 0;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo);
    gl.viewport(0, 0, VIEW_W, VIEW_H);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.enable(gl.BLEND);
    this.applyBlend(this.blend);
  }

  /** Restricts drawing to a rectangle (logical coords), or `null` to clear. */
  setClip(rect: [number, number, number, number] | null): void {
    this.flush();
    const gl = this.gl;
    this.clip = rect;
    if (!rect) {
      gl.disable(gl.SCISSOR_TEST);
      return;
    }
    gl.enable(gl.SCISSOR_TEST);
    const [x, y, w, h] = rect;
    gl.scissor(Math.floor(x), VIEW_H - Math.ceil(y + h), Math.ceil(w), Math.ceil(h));
  }

  draw(s: Sprite, x: number, y: number, o: DrawOpts = {}): void {
    if (!this.atlas.ensure(s)) return;
    const f = s.frame;
    const slot = s.slot!;
    const sc = o.scale ?? 1;
    let x0 = o.topLeft ? x : x + f.offsetX * sc;
    const y0 = o.topLeft ? y : y + f.offsetY * sc;
    if (o.flipX && !o.topLeft) x0 = x - (f.offsetX + f.width) * sc;
    const x1 = x0 + f.width * sc;
    const y1 = y0 + f.height * sc;
    if (x1 < 0 || y1 < 0 || x0 > this.target.w || y0 > this.target.h) return;
    let u0 = slot.x, u1 = slot.x + f.width;
    if (o.flipX) [u0, u1] = [u1, u0];
    const v0 = slot.y, v1 = slot.y + f.height;
    const mode = o.silhouette ? 2 : 0;
    const tint = o.silhouette ?? o.tint ?? WHITE;
    if (o.squashY !== undefined || o.shearX !== undefined) {
      // Project around the anchor (x, y): y' = y + (py - y)·squash, x' = px + (py - y)·shear.
      const sq = o.squashY ?? 1, sh = o.shearX ?? 0;
      const ty0 = (y0 - y) * sq, ty1 = (y1 - y) * sq;
      this.quadSkew(x0 + (y0 - y) * sh, y + ty0, x1 + (y0 - y) * sh, x0 + (y1 - y) * sh, y + ty1, x1 + (y1 - y) * sh, u0, v0, u1, v1, slot.layer, o.palette ?? this.defaultPalette, o.remap ?? -1, mode, tint, o.alpha ?? 1, o.opaqueZero ? 1 : 0, o.blend ?? 'normal');
      return;
    }
    this.quad(x0, y0, x1, y1, u0, v0, u1, v1, slot.layer, o.palette ?? this.defaultPalette, o.remap ?? -1, mode, tint, o.alpha ?? 1, o.opaqueZero ? 1 : 0, o.blend ?? 'normal');
  }

  private quadSkew(
    tlx: number, ty: number, trx: number, blx: number, by: number, brx: number,
    u0: number, v0: number, u1: number, v1: number,
    layer: number, pal: number, remap: number, mode: number,
    tint: readonly [number, number, number], alpha: number, flags: number, blend: BlendMode,
  ): void {
    this.quad(0, 0, 0, 0, u0, v0, u1, v1, layer, pal, remap, mode, tint, alpha, flags, blend);
    // Patch the positions of the quad just written.
    const v = this.verts;
    const o = (this.quads - 1) * 4 * FLOATS_PER_VERT;
    v[o] = tlx;
    v[o + 1] = ty;
    v[o + FLOATS_PER_VERT] = trx;
    v[o + FLOATS_PER_VERT + 1] = ty;
    v[o + 2 * FLOATS_PER_VERT] = blx;
    v[o + 2 * FLOATS_PER_VERT + 1] = by;
    v[o + 3 * FLOATS_PER_VERT] = brx;
    v[o + 3 * FLOATS_PER_VERT + 1] = by;
  }

  fillRect(x: number, y: number, w: number, h: number, rgba: readonly [number, number, number, number], blend: BlendMode = 'normal'): void {
    this.quad(x, y, x + w, y + h, 0, 0, 0, 0, 0, 0, -1, 1, [rgba[0], rgba[1], rgba[2]], rgba[3], 0, blend);
  }

  strokeRect(x: number, y: number, w: number, h: number, rgba: readonly [number, number, number, number]): void {
    this.fillRect(x, y, w, 1, rgba);
    this.fillRect(x, y + h - 1, w, 1, rgba);
    this.fillRect(x, y + 1, 1, h - 2, rgba);
    this.fillRect(x + w - 1, y + 1, 1, h - 2, rgba);
  }

  private quad(
    x0: number, y0: number, x1: number, y1: number,
    u0: number, v0: number, u1: number, v1: number,
    layer: number, pal: number, remap: number, mode: number,
    tint: readonly [number, number, number], alpha: number, flags: number, blend: BlendMode,
  ): void {
    if (blend !== this.blend) {
      this.flush();
      this.blend = blend;
      this.applyBlend(blend);
    }
    if (this.quads >= MAX_QUADS) this.flush();
    const v = this.verts;
    let o = this.quads * 4 * FLOATS_PER_VERT;
    const corners = [
      [x0, y0, u0, v0],
      [x1, y0, u1, v0],
      [x0, y1, u0, v1],
      [x1, y1, u1, v1],
    ];
    for (const c of corners) {
      v[o++] = c[0];
      v[o++] = c[1];
      v[o++] = c[2];
      v[o++] = c[3];
      v[o++] = layer;
      v[o++] = pal;
      v[o++] = remap;
      v[o++] = mode;
      v[o++] = tint[0];
      v[o++] = tint[1];
      v[o++] = tint[2];
      v[o++] = alpha;
      v[o++] = flags;
    }
    this.quads++;
  }

  private applyBlend(b: BlendMode): void {
    const gl = this.gl;
    switch (b) {
      case 'normal':
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        break;
      case 'add':
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
        break;
      case 'multiply':
        gl.blendFunc(gl.DST_COLOR, gl.ONE_MINUS_SRC_ALPHA);
        break;
      case 'screen':
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_COLOR);
        break;
    }
  }

  flush(): void {
    if (this.quads === 0) return;
    const gl = this.gl;
    gl.useProgram(this.prog);
    gl.uniform2f(this.u.uView, this.target.w, this.target.h);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.atlas.texture);
    gl.uniform1i(this.u.uAtlas, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.palettes.texture);
    gl.uniform1i(this.u.uPalettes, 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.remaps.texture);
    gl.uniform1i(this.u.uRemaps, 2);
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.verts, 0, this.quads * 4 * FLOATS_PER_VERT);
    gl.drawElements(gl.TRIANGLES, this.quads * 6, gl.UNSIGNED_INT, 0);
    gl.bindVertexArray(null);
    this.quads = 0;
    this.callsThisFrame++;
  }

  /**
   * Darkens everything drawn so far by the light field (call after the world, before the UI).
   * `ambient` 1 = fully lit.
   */
  applyLighting(ambient: readonly [number, number, number], lights: PointLight[]): void {
    this.flush();
    const gl = this.gl;
    gl.useProgram(this.lightProg);
    gl.uniform3f(this.lightU.uAmbient, ambient[0], ambient[1], ambient[2]);
    const n = Math.min(16, lights.length);
    gl.uniform1i(this.lightU.uCount, n);
    const pos = new Float32Array(64), col = new Float32Array(48);
    for (let i = 0; i < n; i++) {
      const L = lights[i];
      pos.set([L.x, L.y, L.radius, L.intensity], i * 4);
      col.set(L.color ?? [1, 0.92, 0.8], i * 3);
    }
    gl.uniform4fv(this.lightU.uLights, pos);
    gl.uniform3fv(this.lightU.uColors, col);
    gl.uniform2f(this.lightU.uView, VIEW_W, VIEW_H);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.DST_COLOR, gl.ZERO);
    gl.bindVertexArray(this.quadVao);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    gl.bindVertexArray(null);
    this.applyBlend(this.blend);
  }

  /** Finishes the frame: scales the logical framebuffer onto the canvas. */
  end(): void {
    this.flush();
    if (this.clip) this.setClip(null);
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    const vp = this.viewport;
    gl.viewport(vp.x, this.canvas.height - vp.y - vp.h, vp.w, vp.h);
    gl.useProgram(this.presentProg);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.fboTex);
    gl.uniform1i(this.presentU.uTex, 0);
    gl.uniform2f(this.presentU.uTexSize, VIEW_W, VIEW_H);
    gl.uniform1f(this.presentU.uScale, vp.scale);
    gl.uniform1f(this.presentU.uGamma, this.gamma);
    gl.uniform1f(this.presentU.uContrast, this.contrast);
    gl.bindVertexArray(this.quadVao);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    gl.bindVertexArray(null);
    this.drawCalls = this.callsThisFrame;
  }

  /** Reads back the logical framebuffer as RGBA (tests, screenshots). */
  readPixels(): Uint8Array {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo);
    const out = new Uint8Array(VIEW_W * VIEW_H * 4);
    gl.readPixels(0, 0, VIEW_W, VIEW_H, gl.RGBA, gl.UNSIGNED_BYTE, out);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return out;
  }
}

const WHITE = [1, 1, 1] as const;
export { Sprite, ATLAS_SIZE };
